import { TFunction } from 'i18next';
import { Typography, Button, Select, Avatar, FormControl, Dots } from '~/UIKit';
import React from 'react';
import { CHARCOAL_GREY, WHITE } from '../../../../Constants/Colors.constants';
import { Customer } from '../../../../Models/DTO/Customer.model';
import { IProduct } from '../../../../Models/DTO/Product.models';
import { PaymentMethod, PaymentMethodEnumType } from '../../../../Models/DTO/PaymentMethod.model';
import { getInitials } from '../../../../Helpers/String.helper';
import { SelectChangeEvent } from '@mui/material';
import { makeStyles } from '~/UIKit/Style';

export interface ProductDialogComponent {
  selectedProduct: IProduct['id'];
  selectedPaymentMethod: PaymentMethod['enumType'];
  availableProducts: IProduct[];
  availablePaymentMethods: PaymentMethod[];
  customer: Partial<Customer> | null;
  onAddProduct: () => void;
  onSelectPaymentMethod: (ev: SelectChangeEvent<PaymentMethodEnumType>) => void;
  onSelectProduct: (ev: SelectChangeEvent<number>) => void;
  processing: boolean;
  addProductButtonDisabled: boolean;
  t: TFunction;
}

const useStyles = makeStyles({
  root: {
    padding: '29px 15px',
  },
  title: {
    fontWeight: 'bold',
    fontSize: 22,
    lineHeight: '16px',
    letterSpacing: '0.5px',
    color: CHARCOAL_GREY,
  },
  customerDetails: {
    display: 'flex',
    placeItems: 'center',
    marginTop: 30,
    marginBottom: 20,
  },
  customerName: {
    fontSize: 16,
    lineHeight: '21px',
    letterSpacing: '0.4px',
    color: CHARCOAL_GREY,
    fontWeight: 'bold',
    marginLeft: 15,
  },
  selectorLabel: {
    fontSize: 10,
    lineHeight: '12px',
    letterSpacing: 0.5,
    color: CHARCOAL_GREY,
    marginBottom: 6,
  },
  selectorWrapper: {
    width: '100%',
    marginBottom: 20,
  },
  selectorComponent: {},
  confirmButton: {
    width: '100%',
  },
});

const ProductDialogComponent = ({
  selectedPaymentMethod,
  selectedProduct,
  availableProducts,
  availablePaymentMethods,
  customer,
  onAddProduct,
  onSelectProduct,
  onSelectPaymentMethod,
  processing,
  addProductButtonDisabled,
  t,
}: ProductDialogComponent) => {
  const { classes } = useStyles();
  return (
    <div className={classes.root} data-testid="ProductDialog">
      <Typography className={classes.title} data-testid="ProductDialogTitle">
        {t('app.product.addProductFor')}
      </Typography>
      {!customer ? (
        <div>
          <Dots />
        </div>
      ) : (
        <div className={classes.customerDetails}>
          <Avatar src={customer.imageUrl}>
            {getInitials(customer.firstname, customer.lastname)}
          </Avatar>
          <Typography className={classes.customerName}>
            {`${customer.firstname} ${customer.lastname}`}
          </Typography>
        </div>
      )}
      <Typography className={classes.selectorLabel}>{t('app.product')}</Typography>
      <FormControl className={classes.selectorWrapper} variant="outlined">
        <Select<number>
          id="selectProduct"
          inputProps={{
            'aria-label': 'Without label',
          }}
          native
          onChange={onSelectProduct}
          placeholder={t('app.product.choose')}
          value={selectedProduct}
        >
          <option disabled value={-1}>
            {t('app.product')}
          </option>
          {availableProducts.map((product, index) => (
            <option key={index} value={product.id}>
              {product.name}
            </option>
          ))}
        </Select>
      </FormControl>
      {!!selectedProduct && (
        <div>
          <Typography className={classes.selectorLabel} data-testid="selectPayment-label">
            {t('app.payment')}
          </Typography>
          <FormControl className={classes.selectorWrapper} variant="outlined">
            <Select<PaymentMethodEnumType>
              id="selectPayment"
              inputProps={{
                'aria-label': 'Without label',
              }}
              native
              onChange={onSelectPaymentMethod}
              placeholder={t('app.payment.choose')}
              value={selectedPaymentMethod}
            >
              <option aria-label="None" disabled value="">
                {t('app.payment')}
              </option>
              {availablePaymentMethods.map((method, index) => (
                <option key={index} value={method.enumType}>
                  {t(`app.paymentmethod.${method.type}`)}
                </option>
              ))}
            </Select>
          </FormControl>
        </div>
      )}

      <Button
        className={classes.confirmButton}
        color="primary"
        disabled={addProductButtonDisabled}
        onClick={onAddProduct}
        variant="contained"
      >
        {processing ? <Dots color={WHITE} /> : t('app.product.addProduct')}
      </Button>
    </div>
  );
};

export default ProductDialogComponent;
